import { useState } from 'react';
import { UseFormSetValue, UseFormGetValues, UseFormSetError, UseFormClearErrors, UseFormTrigger} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAddressDetails } from '../../../api-refactored/candidate/useGetAdrressDetails';
interface useGetAddressDetailsByPostcodeProps<T> {
  blkNoInputName: string;
  streetInputName: string;
  buildingInputName: string;
  postcodeInputName: string;
  isPostcodeEmptyDefaultValue: boolean;
  setValue?: UseFormSetValue<any>;
  getValues?: UseFormGetValues<any>;
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  trigger?: UseFormTrigger<any>;
}

export const useGetAddressDetailsByPostcode = <T>({
  blkNoInputName,
  streetInputName,
  buildingInputName,
  postcodeInputName,
  isPostcodeEmptyDefaultValue,
  setValue,
  getValues,
  setError,
  clearErrors,
  trigger,
}: useGetAddressDetailsByPostcodeProps<T>) => {
  const { t } = useTranslation();
  const [isDisableAddr, setIsDisableAddr] = useState(true);
  const [postcode, setPostcode] = useState<string>('');
  const [isApiError, setIsApiError] = useState(false);
  const [onFirstLoad, setOnFirstLoad] = useState(true);
  const [isPostalCodeEmpty, setIsPostalCodeEmpty] = useState<boolean>(isPostcodeEmptyDefaultValue);

  const setBlankAddressDetails = () => {
    if (!setValue) {
      return;
    }
    setValue(blkNoInputName, '');
    setValue(streetInputName, '');
    setValue(buildingInputName, '');
  };

  const clearAddressErrors = () => {
    if (!clearErrors) {
      return;
    }
    clearErrors(postcodeInputName);
    clearErrors(blkNoInputName);
    clearErrors(streetInputName);
    clearErrors(buildingInputName);
  };

  const { refetch } = useGetAddressDetails({
    postcode,
    enabled: !onFirstLoad,
    onSuccess: (res) => {
      if (!setValue || !setError) {
        return;
      }
      setIsApiError(false);
      if (res.results.length === 0) {
        setBlankAddressDetails();
        setError(postcodeInputName, {
          type: 'custom',
          message: t('profile->>CandidateProfileDetail.InvalidPostcodeMsg'),
        });
      } else {
        const firstResult = res.results[0];
        const blkNo = firstResult.BLK_NO;
        const streetName = firstResult.ROAD_NAME;
        setValue(blkNoInputName, blkNo);
        setValue(streetInputName, streetName);
        setValue(buildingInputName, '');
        clearAddressErrors();
      }
      setIsDisableAddr(true);
    },
    onError: (_) => {
      if (!setError) {
        return;
      }
      setBlankAddressDetails();
      setIsApiError(true);
      setIsDisableAddr(false);
    },
  });

  const validatePostalCode = () => {
    if (!setValue || !getValues || !trigger) {
      return;
    }
    const enteredPostcode = getValues(postcodeInputName as string) as string;
    if (enteredPostcode.length !== 6) {
      trigger(postcodeInputName).then();
    } else if (enteredPostcode === '000000') {
      setValue(blkNoInputName, 'N.A.');
      setValue(streetInputName, 'N.A.');
      setValue(buildingInputName, 'N.A.');
      setIsApiError(false);
      setIsDisableAddr(true);
      clearAddressErrors();
    } else {
      setOnFirstLoad(false);
      if (enteredPostcode === postcode) {
        refetch().then();
      } else {
        setPostcode(enteredPostcode);
      }
    }
  };

  const postcodeOnChange = (postcodeValue: string) => {
    setIsPostalCodeEmpty(postcodeValue.trim() === '');
    setBlankAddressDetails();
  };

  return {
    isDisableAddr,
    isApiError,
    validatePostalCode,
    isPostalCodeEmpty,
    setIsPostalCodeEmpty,
    postcodeOnChange,
  };
};
